import React from 'react'

import { Box, Stack, Tooltip, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'

import Iconify from '../Iconify/Iconify'

type ChangeIndicatorProps = {
  value?: number | null
  size?: 'small' | 'medium'
  align?: 'flex-start' | 'center' | 'flex-end'
  currentValue?: number | null
  previousValue?: number | null
  formatValue?: (value: number) => string
}

const getVisuals = (value: number) => {
  if (value > 0) {
    return {
      color: 'success.main',
      icon: 'eva:trending-up-fill',
      prefix: '+',
    }
  }

  if (value < 0) {
    return {
      color: 'error.main',
      icon: 'eva:trending-down-fill',
      prefix: '−',
    }
  }

  return {
    color: 'text.secondary',
    icon: 'eva:minus-fill',
    prefix: '',
  }
}

const computePercentChange = (current: number, previous: number) => {
  if (previous === 0) {
    if (current === 0) return 0
    return 100 // from 0 → something = +100%
  }
  return ((current - previous) / previous) * 100
}

const defaultFormatter = (input: number) =>
  Number.isFinite(input) ? input.toLocaleString() : String(input)

export const ChangeIndicator: React.FC<ChangeIndicatorProps> = ({
  value,
  size = 'medium',
  align = 'center',
  currentValue,
  previousValue,
  formatValue,
}) => {
  const { t } = useTranslation()

  if (value === null || value === undefined || Number.isNaN(value)) {
    return null
  }

  const iconSize = size === 'small' ? 16 : 20
  const variant = size === 'small' ? 'caption' : 'body2'
  const percent = computePercentChange(currentValue ?? 0, previousValue ?? 0)

  const { color, icon, prefix } = getVisuals(value)

  const formatted = `${prefix}${Math.abs(percent).toFixed(1)}%`

  const format = formatValue ?? defaultFormatter

  const hasCurrentValue =
    currentValue !== null && currentValue !== undefined && Number.isFinite(currentValue)
  const hasPreviousValue =
    previousValue !== null && previousValue !== undefined && Number.isFinite(previousValue)

  // 🧮 NEW: calculate numeric difference if both values are valid
  const hasBoth = hasCurrentValue && hasPreviousValue
  let numericChange: number | null = null

  if (hasBoth) {
    numericChange = (currentValue as number) - (previousValue as number)
  }

  const tooltipContent =
    hasCurrentValue || hasPreviousValue ? (
      <Stack spacing={0.5}>
        {hasCurrentValue && (
          <Typography variant="caption" color="inherit">
            {t('adminDashboardPage.changeIndicator.current')}: {format(currentValue as number)}
          </Typography>
        )}
        {hasPreviousValue && (
          <Typography variant="caption" color="inherit">
            {t('adminDashboardPage.changeIndicator.comparison')}: {format(previousValue as number)}
          </Typography>
        )}
        {numericChange !== null && (
          <Typography variant="caption">
            {t('adminDashboardPage.changeIndicator.change')}: {numericChange > 0 ? '+' : ''}
            {format(numericChange)}
          </Typography>
        )}
      </Stack>
    ) : null

  const content = (
    <Stack
      direction="row"
      spacing={0.5}
      alignItems="center"
      justifyContent={align}
      sx={{ cursor: 'pointer' }}
    >
      <Iconify icon={icon} width={iconSize} height={iconSize} sx={{ color }} />
      <Typography variant={variant} color={color} fontWeight={500}>
        {formatted}
      </Typography>
    </Stack>
  )

  if (!tooltipContent) {
    return content
  }

  return (
    <Tooltip title={tooltipContent} arrow placement="top">
      <Box component="span" sx={{ display: 'inline-flex' }}>
        {content}
      </Box>
    </Tooltip>
  )
}

export default ChangeIndicator
